import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { SiGoogledrive } from "react-icons/si";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { GoogleDrivePicker } from "@/components/GoogleDrivePicker";
import { useToast } from "@/hooks/use-toast";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { foldersService } from "@/services/foldersService";
import { knowledgeBaseService } from "@/services/knowledgeBaseService";

type DriveQuickImportFile = {
  text?: string;
  filename?: string;
  folderPath?: string;
};

type NoteCreationProgress = {
  total: number;
  completed: number;
  current: string;
  failed: string[];
};

type GoogleDriveQuickImportProps = {
  variant?: "sidebar" | "page";
};

export function GoogleDriveQuickImport({
  variant = "page",
}: GoogleDriveQuickImportProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { currentWorkspaceId } = useWorkspace();
  const [isCreatingNotes, setIsCreatingNotes] = useState(false);
  const [noteCreationProgress, setNoteCreationProgress] =
    useState<NoteCreationProgress | null>(null);
  const [showDrivePicker, setShowDrivePicker] = useState(true);

  const handleFilesSelected = async (files: DriveQuickImportFile[]) => {
    const validFiles = files.filter(
      (file) =>
        file.text &&
        typeof file.text === "string" &&
        file.text.trim().length > 0
    );

    if (validFiles.length === 0) {
      toast({
        title: "No valid files",
        description: "No files with extractable text were found.",
        variant: "destructive",
      });
      return;
    }

    const getFolderPathFromFile = (
      folderPath: string | undefined
    ): string | null => {
      if (!folderPath || !folderPath.trim()) return null;
      return folderPath.trim();
    };

    const extractFolderPaths = (importFiles: DriveQuickImportFile[]): string[] => {
      const folderPaths = new Set<string>();

      importFiles.forEach((file) => {
        const folderPath = getFolderPathFromFile(file.folderPath);
        if (folderPath) {
          folderPaths.add(folderPath);
          const parts = folderPath.split("/");
          for (let i = 1; i < parts.length; i++) {
            folderPaths.add(parts.slice(0, i).join("/"));
          }
        }
      });

      return Array.from(folderPaths).sort((a, b) => {
        const depthA = a.split("/").length;
        const depthB = b.split("/").length;
        return depthA - depthB;
      });
    };

    const createFolderHierarchy = async (
      folderPaths: string[]
    ): Promise<Map<string, string>> => {
      const folderMap = new Map<string, string>();
      const existingFolders = await foldersService.getFolders(
        currentWorkspaceId ?? null
      );

      for (const folderPath of folderPaths) {
        const pathParts = folderPath.split("/");
        const folderName = pathParts[pathParts.length - 1];
        const parentPath =
          pathParts.length > 1 ? pathParts.slice(0, -1).join("/") : null;
        const parentFolderId = parentPath
          ? folderMap.get(parentPath) || null
          : null;

        const existingFolder = existingFolders.find(
          (f) =>
            f.name.toLowerCase() === folderName.toLowerCase() &&
            (f.parentId === parentFolderId ||
              (!f.parentId && !parentFolderId))
        );

        if (existingFolder) {
          folderMap.set(folderPath, existingFolder.id);
        } else {
          try {
            setNoteCreationProgress((prev) =>
              prev
                ? { ...prev, current: `Creating folder: ${folderPath}` }
                : null
            );

            const newFolder = await foldersService.createFolder(
              { name: folderName, parentId: parentFolderId },
              currentWorkspaceId ?? null
            );
            folderMap.set(folderPath, newFolder.id);
            existingFolders.push(newFolder);
          } catch (error) {
            console.error(`Error creating folder ${folderPath}:`, error);
          }
        }
      }

      return folderMap;
    };

    const folderPaths = extractFolderPaths(validFiles);
    let folderPathToIdMap = new Map<string, string>();

    if (folderPaths.length > 0) {
      try {
        setNoteCreationProgress((prev) =>
          prev ? { ...prev, current: "Creating folder structure..." } : null
        );

        folderPathToIdMap = await createFolderHierarchy(folderPaths);
        queryClient.invalidateQueries({ queryKey: ["/api/library-folders"] });

        toast({
          title: "Folders created",
          description: `Created ${folderPaths.length} folder(s)`,
        });
      } catch (error) {
        const message =
          error instanceof Error ? error.message : "Unknown error";
        console.error("Error creating folder hierarchy:", error);
        toast({
          title: "Folder error",
          description: `Could not create folders: ${message}`,
          variant: "destructive",
        });
      }
    }

    setShowDrivePicker(false);
    setIsCreatingNotes(true);
    setNoteCreationProgress({
      total: validFiles.length,
      completed: 0,
      current: "",
      failed: [],
    });

    let successCount = 0;
    let failCount = 0;

    for (let i = 0; i < validFiles.length; i++) {
      const file = validFiles[i];
      const filename = file.filename || `Untitled Document ${i + 1}`;

      setNoteCreationProgress((prev) =>
        prev
          ? { ...prev, current: `Creating note: ${filename}`, completed: i }
          : null
      );

      try {
        const fileFolderPath = file.folderPath
          ? getFolderPathFromFile(file.folderPath)
          : undefined;
        const fileFolderId = fileFolderPath
          ? folderPathToIdMap.get(fileFolderPath) || null
          : null;

        await knowledgeBaseService.createKnowledgeBaseItem(
          {
            title: filename,
            content: file.text ?? "",
            type: "note",
            spaceId: null,
            topicId: null,
            tagIds: [],
            folderId: fileFolderId,
          },
          currentWorkspaceId ?? null
        );

        successCount++;
        queryClient.invalidateQueries({
          predicate: (query) => {
            const key = query.queryKey[0];
            return (
              typeof key === "string" &&
              (key === "/api/knowledge-base" ||
                key === "/api/knowledgebase" ||
                key === "knowledgebase" ||
                key === "knowledgebase-summary")
            );
          },
        });
      } catch (error) {
        console.error(`Error creating note for ${filename}:`, error);
        failCount++;
        setNoteCreationProgress((prev) =>
          prev ? { ...prev, failed: [...prev.failed, filename] } : null
        );
      }
    }

    setNoteCreationProgress((prev) =>
      prev
        ? { ...prev, completed: validFiles.length, current: "Complete!" }
        : null
    );

    if (successCount > 0) {
      const folderCount = folderPathToIdMap.size;
      const folderMessage =
        folderCount > 0 ? ` in ${folderCount} folder(s)` : "";
      toast({
        title: "Notes created",
        description: `${successCount} note(s) created successfully${folderMessage}${
          failCount > 0 ? `. ${failCount} failed.` : "."
        }`,
      });
    }

    if (failCount > 0 && successCount === 0) {
      toast({
        title: "Creation failed",
        description: `Failed to create ${failCount} note(s).`,
        variant: "destructive",
      });
    }

    setTimeout(() => {
      setIsCreatingNotes(false);
      setNoteCreationProgress(null);
      setShowDrivePicker(true);
    }, 2000);
  };

  if (!showDrivePicker) {
    return (
      <div
        className={
          variant === "sidebar"
            ? "space-y-3 p-4 border rounded-lg bg-muted/30"
            : "space-y-3 p-4 border rounded-lg bg-muted/30 max-w-md"
        }
        style={variant === "sidebar" ? { marginLeft: "24px" } : undefined}
      >
        {isCreatingNotes && noteCreationProgress && (
          <>
            <div className="flex items-center justify-between text-sm">
              <span className="font-medium">Creating notes...</span>
              <span className="text-muted-foreground">
                {noteCreationProgress.completed} / {noteCreationProgress.total}
              </span>
            </div>
            <Progress
              value={
                (noteCreationProgress.completed / noteCreationProgress.total) *
                100
              }
              className="h-2"
            />
            <p className="text-xs text-muted-foreground truncate">
              {noteCreationProgress.current}
            </p>
            {noteCreationProgress.failed.length > 0 && (
              <p className="text-xs text-destructive">
                Failed: {noteCreationProgress.failed.length} file(s)
              </p>
            )}
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Please wait...</span>
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <GoogleDrivePicker
      multiple={true}
      isFinalConfirmation={true}
      extractTextOnly={true}
      onFilesSelected={handleFilesSelected}
    >
      <Button
        variant={variant === "sidebar" ? "ghost" : "outline"}
        size="sm"
        className={
          variant === "sidebar"
            ? "w-full justify-start gap-2"
            : "gap-2"
        }
        style={variant === "sidebar" ? { paddingLeft: "48px" } : undefined}
        data-testid="button-sync-drive"
      >
        <SiGoogledrive className="h-4 w-4" />
        Quick import from Drive
      </Button>
    </GoogleDrivePicker>
  );
}
